<template>
  <div class="toast-container" aria-live="polite">
    <transition-group name="toast" tag="div">
      <div
        v-for="t in toasts"
        :key="t.id"
        class="toast"
        :class="`toast--${t.type}`"
        @click="remove(t.id)"
      >
        <div class="toast__icon-wrap">
          <v-icon class="toast__icon">{{ icons[t.type] }}</v-icon>
        </div>
        <div class="toast__body">
          <p class="toast__title">{{ labels[t.type] }}</p>
          <p class="toast__msg">{{ t.message }}</p>
        </div>
        <button
          class="toast__close"
          @click.stop="remove(t.id)"
          aria-label="Cerrar"
        >
          <v-icon small>close</v-icon>
        </button>
        <div
          class="toast__progress"
          :style="{ animationDuration: t.timeout + 'ms' }"
        ></div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'AppToast',
  data() {
    return {
      toasts: [],
      icons: {
        success: 'check_circle',
        error: 'error',
        warning: 'warning',
        info: 'info',
      },
      labels: {
        success: 'Éxito',
        error: 'Error',
        warning: 'Advertencia',
        info: 'Información',
      },
    }
  },
  methods: {
    add({ type = 'info', message = '', timeout = 4000 }) {
      const id = Date.now() + Math.random()
      this.toasts.push({ id, type, message, timeout })
      setTimeout(() => this.remove(id), timeout)
    },
    remove(id) {
      const idx = this.toasts.findIndex(t => t.id === id)
      if (idx !== -1) this.toasts.splice(idx, 1)
    },
  },
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
  max-width: 360px;
  width: 100%;
}

/* ── Toast card ── */
.toast {
  display: flex;
  align-items: flex-start;
  gap: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.14),
    0 2px 8px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  position: relative;
  pointer-events: all;
  cursor: pointer;
  border: 1px solid #e2e8f0;
}

/* colored left bar */
.toast::before {
  content: '';
  width: 4px;
  min-height: 100%;
  flex-shrink: 0;
  display: block;
}

.toast--success::before {
  background: #22c55e;
}
.toast--error::before {
  background: #e5252a;
}
.toast--warning::before {
  background: #c79a2b;
}
.toast--info::before {
  background: #254f87;
}

/* ── Icon ── */
.toast__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 10px 14px 14px;
  flex-shrink: 0;
}

.toast--success .toast__icon {
  color: #22c55e !important;
}
.toast--error .toast__icon {
  color: #e5252a !important;
}
.toast--warning .toast__icon {
  color: #c79a2b !important;
}
.toast--info .toast__icon {
  color: #254f87 !important;
}

.toast__icon {
  font-size: 22px !important;
}

/* ── Body ── */
.toast__body {
  flex: 1;
  padding: 12px 4px 12px 0;
  min-width: 0;
}

.toast__title {
  margin: 0 0 2px;
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.toast__msg {
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
  word-break: break-word;
}

/* ── Close ── */
.toast__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 12px 10px 4px;
  color: #94a3b8;
  flex-shrink: 0;
  line-height: 1;
  align-self: flex-start;
  margin-top: 6px;
}

.toast__close:hover {
  color: #475569;
}

/* ── Progress bar ── */
.toast__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  transform-origin: left;
  animation: toast-progress linear forwards;
  border-radius: 0 0 12px 12px;
}

.toast--success .toast__progress {
  background: #22c55e;
}
.toast--error .toast__progress {
  background: #e5252a;
}
.toast--warning .toast__progress {
  background: #c79a2b;
}
.toast--info .toast__progress {
  background: #254f87;
}

@keyframes toast-progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* ── Transition ── */
.toast-enter-active {
  animation: toast-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.toast-leave-active {
  animation: toast-out 0.25s ease forwards;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(120%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes toast-out {
  from {
    opacity: 1;
    transform: translateX(0);
    max-height: 120px;
    margin-bottom: 0;
  }
  to {
    opacity: 0;
    transform: translateX(120%);
    max-height: 0;
    margin-bottom: -10px;
  }
}

@media (max-width: 480px) {
  .toast-container {
    top: 12px;
    right: 12px;
    left: 12px;
    max-width: none;
  }
}
</style>
